// components/SingleSelectAdd.js
import React from 'react';
import { Close } from '@mui/icons-material';
import { Divider, Grid } from '@mui/material';
import { makeStyles, Typography } from '@material-ui/core';
import { ReactComponent as Trash } from '../../../assets/trash.svg';
import CustomTextField from '../../shared/CustomTextField';
import CustomButton from '../../shared/CustomButton';
import CustomPopupText from '../../shared/CustomPopupText';
import CustomRadio from '../../shared/CustomRadio';
import QuestionChip from './QuestionChip';
import CustomPicker from '../../shared/CustomPicker';
import { generateEmptyQuestion } from '../utils';
import { ErrorToast } from '../../utils/Utils';

const useStyles = makeStyles({
  publishDateField: {
    height: '44px',
    padding: '0px 12px',
    fontSize: '12px',
    margin: '12px 0px',
    border: '1px solid rgba(228, 231, 236, 1)',
    borderRadius: '8px',
    background: 'rgba(254, 254, 254, 1)',
  },
  trash: {
    width: '32px',
    height: '32px',
    gap: '24px',
    borderRadius: '4px',
    borderWidth: '1px',
    background: '#FEFEFE',
    border: '1px solid #E4E7EC',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  subTitle: {
    fontFamily: 'Lato',
    fontWeight: 700,
    fontSize: '16px',
    lineHeight: '140%',
    letterSpacing: '0%',
  },
  charCount: {
    fontFamily: 'Lato',
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '16.8px',
    color: 'rgba(0, 0, 0, 0.4)',
    textAlign: 'start',
    float: 'right',
    marginRight: '5px',
  },
});

const CHAR_LIMIT = {
  QUESTION: 350,
  OPTION: 255,
};

const SingleSelectAdd = ({ data, setData, handleDateChange, handleData }) => {
  const classes = useStyles();

  const handleQuestionTitleChange = (index, value) => {
    const capped = value.slice(0, CHAR_LIMIT.QUESTION);
    const updated = [...(data?.questions ?? [])];
    updated[index].title = capped;
    setData((prev) => ({ ...prev, questions: updated }));
  };

  const handleOptionChange = (qIndex, oIdOrIndex, value) => {
    const capped = value.slice(0, CHAR_LIMIT.OPTION);
    const updated = [...(data?.questions ?? [])];

    updated[qIndex].options = updated[qIndex].options.map((opt, oIndex) => {
      const match = opt.id ? opt.id === oIdOrIndex : oIndex === oIdOrIndex;
      return match ? { ...opt, title: capped } : opt;
    });

    setData((prev) => ({ ...prev, questions: updated }));
  };

  const handleMarkCorrectAnswer = (qIndex, oIdOrIndex) => {
    const updated = [...(data?.questions ?? [])];
    updated[qIndex].options = updated[qIndex].options.map((opt, oIndex) => ({
      ...opt,
      isAnswer: opt.id ? opt.id === oIdOrIndex : oIndex === oIdOrIndex,
    }));
    setData((prev) => ({ ...prev, questions: updated }));
  };

  const handleRemoveQuestion = (qIndex) => {
    const updated = [...(data?.questions ?? [])];
    updated.splice(qIndex, 1);
    setData((prev) => ({ ...prev, questions: updated }));
  };

  const handleRemoveOption = (qIndex, oIdOrIndex) => {
    const updated = [...(data?.questions ?? [])];
    if (updated[qIndex].options?.length > 2) {
      updated[qIndex].options = updated[qIndex].options.filter((opt, oIndex) =>
        opt.id ? opt.id !== oIdOrIndex : oIndex !== oIdOrIndex,
      );
      setData((prev) => ({ ...prev, questions: updated }));
    } else {
      ErrorToast('At least 2 options are required.');
    }
  };

  const handleAddMoreOption = (qIndex) => {
    const updated = [...(data?.questions ?? [])];
    updated[qIndex].options.push({ title: '', isAnswer: false });
    setData((prev) => ({ ...prev, questions: updated }));
  };

  const handleAddQuestion = () => {
    setData((prev) => ({ ...prev, questions: [...prev.questions, generateEmptyQuestion()] }));
  };

  return (
    <Grid container direction="column" spacing={1}>
      <Grid item>
        <Grid container direction="column">
          <Grid item>
            <Grid container direction="row" spacing={2}>
              <Grid item xs={6}>
                <CustomPopupText text="Quiz Name" required />
                <CustomTextField
                  placeholder="Enter quiz name"
                  value={data?.name}
                  handleChange={handleData}
                  name="name"
                  width="100%"
                />
              </Grid>
              <Grid item xs={6}>
                <CustomPopupText text="Duration (in mins)" required />
                <CustomTextField
                  placeholder="Enter duration"
                  value={data?.duration}
                  handleChange={handleData}
                  name="duration"
                  width="100%"
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <Grid container direction="row">
              <Grid item xs={6}>
                <CustomPopupText text="Publish Date" required />
                <CustomPicker
                  name="publishDate"
                  data={data}
                  handleDateChange={handleDateChange}
                  customStyles={classes.publishDateField}
                />
              </Grid>
              <Grid item xs={6}>
                <CustomPopupText text="Number of questions" required />
                <CustomTextField
                  placeholder="Enter number of questions"
                  value={data?.numberOfQuestions}
                  handleChange={handleData}
                  name="numberOfQuestions"
                  width="100%"
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid item>
        <Typography className={classes.subTitle}>Quiz Questions</Typography>
      </Grid>
      {data?.questions?.map((question, qIndex) => (
        <Grid item key={question.id}>
          <Grid container direction="column">
            <Grid item>
              <Grid container direction="row" justifyContent="space-between" alignItems="center">
                <Grid item>
                  <QuestionChip title={`Question ${qIndex + 1}`} />
                </Grid>
                <Grid item>
                  <Grid container gap={1} flexDirection="row">
                    <Grid item>
                      <Typography className={classes.charCount} style={{ marginTop: '8px' }}>
                        {`${question.title?.length || 0}/${CHAR_LIMIT.QUESTION}`}
                      </Typography>
                    </Grid>
                    <Grid item className={classes.trash} onClick={() => handleRemoveQuestion(qIndex)}>
                      <Trash />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <CustomTextField
                placeholder="Write Question here"
                value={question.title}
                width="100%"
                handleChange={({ target: { value } }) => handleQuestionTitleChange(qIndex, value)}
              />
            </Grid>
            {question.options.map((option, oIndex) => (
              <Grid item key={option.id} style={{ height: '75px' }}>
                <Grid container alignItems="center">
                  <Grid item xs={9}>
                    <Grid container gap={1} flexDirection="row">
                      <Grid item xs={11}>
                        <CustomTextField
                          placeholder={`Option ${oIndex + 1}`}
                          value={option.title}
                          width="100%"
                          handleChange={({ target: { value } }) =>
                            handleOptionChange(qIndex, option.id ?? oIndex, value)
                          }
                        />
                      </Grid>
                      <Grid item style={{ display: 'flex', alignItems: 'center' }}>
                        <Typography className={classes.charCount}>
                          {`${option.title?.length || 0}/${CHAR_LIMIT.OPTION}`}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={0.5}>
                    <CustomRadio
                      checked={option.isAnswer}
                      onChange={() => handleMarkCorrectAnswer(qIndex, option.id ?? oIndex)}
                      name={`correct-option-${question.id}`}
                      inputProps={{ 'aria-label': 'Select correct answer' }}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <CustomPopupText text="Mark as correct answer" />
                  </Grid>
                  <Grid item xs={0.5}>
                    <Close
                      fontSize="small"
                      onClick={() => handleRemoveOption(qIndex, option.id ?? oIndex)}
                      style={{ cursor: 'pointer' }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            ))}
            <Grid item>
              <CustomButton
                variant="text"
                label="+ Add Option"
                size="small"
                fontSize="10px"
                onClick={() => handleAddMoreOption(qIndex)}
              />
            </Grid>
          </Grid>
        </Grid>
      ))}

      <Grid item>
        <Grid container alignItems="center" spacing={0.5}>
          <Grid item xs>
            <Divider />
          </Grid>
          <Grid item>
            <CustomButton label="+ Add Question" variant="outlined" onClick={handleAddQuestion} />
          </Grid>
          <Grid item xs>
            <Divider />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default SingleSelectAdd;
